import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import '@/components/ui/styles/customScrollBar.css';
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from 'date-fns';
import { AlertCircle, CheckCircle2, Download, Eye } from 'lucide-react';
import React from 'react';

const getBooleanDisplay = (value) => {
   if (value === undefined || value === null) return 'N/A';
   return value ? 'Yes' : 'No';
};

const CSAWDetailsRenderer = ({ data, handlePreview, handleDownload, documentsOnRight }) => {
   const formatDateString = (dateString) => {
      try {
         const date = new Date(parseInt(dateString));
         if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateString);
            return 'Date not available';
         }
         return format(date, 'MMMM d, yyyy');
      } catch (error) {
         console.error('Error formatting date:', error);
         return 'Date not available';
      }
   };

   const renderFileSection = (files, title) => {
      if (!files || !Array.isArray(files) || files.length === 0) return null;

      return (
         <div className="space-y-2">
            <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">{title}</h4>
            <div className="grid grid-cols-1 gap-2">
               {files.map((file, index) => (
                  <div
                     key={index}
                     className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-3 rounded-lg"
                  >
                     <span className="text-sm text-gray-600 dark:text-gray-300">{file.filename}</span>
                     <div className="flex gap-2">
                        <TooltipProvider delayDuration={250}>
                           <Tooltip>
                              <TooltipTrigger asChild>
                                 <Button
                                    size="icon"
                                    variant="outline"
                                    className="dark:border-gray-600 dark:hover:bg-gray-700"
                                    onClick={() => handlePreview(file)}
                                 >
                                    <Eye className="h-4 w-4" />
                                 </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                 <p>Preview</p>
                              </TooltipContent>
                           </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider delayDuration={250}>
                           <Tooltip>
                              <TooltipTrigger asChild>
                                 <Button
                                    size="icon"
                                    variant="outline"
                                    className="dark:border-gray-600 dark:hover:bg-gray-700"
                                    onClick={() => handleDownload(file)}
                                 >
                                    <Download className="h-4 w-4" />
                                 </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                 <p>Download</p>
                              </TooltipContent>
                           </Tooltip>
                        </TooltipProvider>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      );
   };

   const processFiles = (files) => {
      if (!files) return [];
      return Array.isArray(files) ? files : [files];
   };

   // Define base required documents
   const baseRequiredDocuments = {
      officialReceipt: "Official Receipt",
      deedOfSale: "Deed of Sale"
   };

   // Define conditional documents based on user selections
   const conditionalDocuments = {
      specialPowerOfAttorney: {
         label: "Special Power of Attorney",
         required: !data.isOwner
      },
      forestTenureAgreement: {
         label: "Forest Tenure Agreement",
         required: data.isTenureHolder
      },
      businessPermit: {
         label: "Business Permit",
         required: data.isBusinessOwner
      },
      certificateOfRegistration: {
         label: "Certificate of Registration",
         required: data.isPTPRHolder
      },
      woodProcessingPlantPermit: {
         label: "Wood Processing Plant Permit",
         required: data.isWPPHolder
      }
   };

   // Get all required documents based on conditions
   const getAllRequiredDocuments = () => {
      const required = { ...baseRequiredDocuments };

      Object.entries(conditionalDocuments).forEach(([key, doc]) => {
         if (doc.required) {
            required[key] = doc.label;
         }
      });

      return required;
   };

   // Check for missing required documents
   const missingDocuments = Object.entries(getAllRequiredDocuments())
      .filter(([key]) => {
         return !data.files?.[key] || data.files[key].length === 0;
      });

   // Check if all required documents are complete
   const isDocumentationComplete = missingDocuments.length === 0;

   return (
      <>
         {missingDocuments.length > 0 ? (
            <Alert variant="destructive" className="mb-4 dark:bg-red-900/20 dark:border-red-900 dark:text-red-200">
               <AlertCircle className="h-4 w-4 dark:text-red-200" />
               <AlertTitle className="dark:text-red-200">Missing Required Documents</AlertTitle>
               <AlertDescription className="dark:text-red-200/90">
                  The following required documents are missing:
                  <ul className="list-disc list-inside mt-2">
                     {missingDocuments.map(([key, label]) => (
                        <li key={key}>{label}</li>
                     ))}
                  </ul>
               </AlertDescription>
            </Alert>
         ) : (
            <Alert variant="success" className="mb-4 bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-900">
               <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
               <AlertTitle className="text-green-600 dark:text-green-400">Documentation Complete</AlertTitle>
               <AlertDescription className="text-green-600 dark:text-green-400/90">
                  All required documents have been submitted.
               </AlertDescription>
            </Alert>
         )}

         <div className="grid grid-cols-2 gap-4">
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Name</h3>
               <p className="dark:text-gray-200">{data.ownerName || 'N/A'}</p>
            </div>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Address</h3>
               <p className="dark:text-gray-200">{data.address || 'N/A'}</p>
            </div>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Contact Number</h3>
               <p className="dark:text-gray-200">{data.phone || 'N/A'}</p>
            </div>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Registration Type</h3>
               <p className="dark:text-gray-200">{data.registrationType || 'N/A'}</p>
            </div>
         </div>

         <Separator className="my-4 dark:bg-gray-700" />

         <div className="space-y-4">
            <h3 className="font-semibold dark:text-gray-200">Purpose</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
               {data.purpose || 'N/A'}
            </p>
         </div>

         <Separator className="my-4 dark:bg-gray-700" />

         <div className="space-y-4">
            <h3 className="font-semibold dark:text-gray-200">Chainsaw Information</h3>
            <div className="grid grid-cols-2 gap-4">
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Chainsaw Store</h3>
                  <p className="dark:text-gray-200">{data.chainsawStore || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Brand</h3>
                  <p className="dark:text-gray-200">{data.brand || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Model</h3>
                  <p className="dark:text-gray-200">{data.model || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Serial Number</h3>
                  <p className="dark:text-gray-200">{data.serialNumber || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Date of Acquisition</h3>
                  <p className="dark:text-gray-200">{data.dateOfAcquisition ? formatDateString(data.dateOfAcquisition) : 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Power Output</h3>
                  <p className="dark:text-gray-200">{data.powerOutput || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Max Length Guidebar</h3>
                  <p className="dark:text-gray-200">{data.maxLengthGuidebar || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Country of Origin</h3>
                  <p className="dark:text-gray-200">{data.countryOfOrigin || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Purchase Price</h3>
                  <p className="dark:text-gray-200">
                     {data.purchasePrice ? `₱${data.purchasePrice.toLocaleString()}` : 'N/A'}
                  </p>
               </div>
            </div>
         </div>

         <Separator className="my-4 dark:bg-gray-700" />

         <div className="space-y-4">
            <h3 className="font-semibold dark:text-gray-200">Owner Status</h3>
            <div className="grid grid-cols-2 gap-4">
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Owner</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.isOwner)}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Tenure Holder</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.isTenureHolder)}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Business Owner</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.isBusinessOwner)}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">PTPR Holder</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.isPTPRHolder)}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">WPP Holder</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.isWPPHolder)}</p>
               </div>
            </div>
         </div>

         {!documentsOnRight && (
            <>
               <Separator className="my-4 dark:bg-gray-700" />
               <div className="space-y-4">
                  <h3 className="font-semibold dark:text-gray-200">Supporting Documents</h3>
                  {data.files && (
                     <ScrollArea className="h-[300px] custom-scrollbar">
                        <div className="space-y-4 pr-4">
                           {renderFileSection(processFiles(data.files.letterOfIntent), "Letter of Intent")}
                           {renderFileSection(processFiles(data.files.applicationLetter), "Application Letter")}
                           {renderFileSection(processFiles(data.files.lguEndorsement), "LGU Endorsement")}
                           {renderFileSection(processFiles(data.files.titleCertificate), "Title Certificate")}
                           {renderFileSection(processFiles(data.files.darCertificate), "DAR Certificate")}
                           {renderFileSection(processFiles(data.files.specialPowerOfAttorney), "Special Power of Attorney")}
                           {renderFileSection(processFiles(data.files.ptaResolution), "PTA Resolution")}
                        </div>
                     </ScrollArea>
                  )}
               </div>
            </>
         )}
      </>
   );
};

export default CSAWDetailsRenderer;
